/**
 * "Who are you, and where are you?" and the personal path (owner, 2026-10-01: "better onboarding with progressive
 * disclosure… I want the biggest benefits fastest, and to understand a proper workflow for 'me', depending on who I am
 * and what stage of business or home use I am at… with an ontological reason why").
 *
 * Pure functions, shared by the web, the phone and the API (T7: one truth).
 *
 * Rules (test/journey.test.ts, each with a control):
 * | J1 Not sure is never a fact | an answer is a value or NOT_SURE. Setup stores the guesses it had to make (county,
 *   permit, hours, menu) in Location.unconfirmed; `factLine` says "Not set yet" / "To confirm", never the guess as fact |
 * | J2 Asked when it matters | an unconfirmed fact becomes a path item only from the stage where it decides something
 *   (FACT_MOMENT): the county at "building" (plan review), hours and menu at "about to open" (the daily checks) |
 * | J3 Planning mode | planning, building and about-to-open places have no daily checks due and nothing late
 *   (`scheduleHours` gives every day closed), and no food-handler clock (`foodWorkStart` is null) |
 * | J4 We're open | the server sets openedOn when a place moves from planning to operating; the clock starts there |
 * | J5 Path by persona × stage | `personalPath` keeps only the items for this who, stage and role, ranks them by
 *   benefit per minute, and shows the first 3; the rest unlock as those are done or put off |
 * | J6 Every item says why | each item has one plain sentence on the good it serves, then one action |
 */
import type { IsoDate, Location, OrgKind, PermitType } from "./model.ts";
import type { WeeklyHours } from "./hours.ts";
import { JOURNEY_UT } from "./states/ut/journey.ts";
import { perStateTable } from "./states/access.ts";
import type { StateWords, UsState } from "./states/types.ts";
import { countyLabel, stateOrDefault } from "./states/index.ts";
import { cite, wordsFor } from "./states/words.ts";
import { AHEAD, AHEAD_MAX, isAheadItem } from "./ahead.ts";

const WORDS: Record<UsState, StateWords<typeof JOURNEY_UT>> = perStateTable("JOURNEY", JOURNEY_UT);
const words = (state?: UsState) => wordsFor(WORDS, state);

/* ─────────────── the answers ─────────────── */

/** Who the place is for. "Something else" types (catering, bar, BBQ…) fold into the nearest one. */
export type JourneyWho = "home" | "coffee_shop" | "restaurant" | "food_truck" | "church_kitchen" | "event_booth";
export type JourneyRole = "owner" | "manager" | "shift_lead" | "staff";
export type BusinessStage = "planning" | "building" | "about_to_open" | "open_new" | "open_running";
export type HomeStage = "just_moved" | "settled";
export type JourneyStage = BusinessStage | HomeStage;
/** "Not sure" is always allowed (J1). */
export const NOT_SURE = "not_sure" as const;
export type NotSure = typeof NOT_SURE;
export type Maybe<T> = T | NotSure;

export const BUSINESS_STAGES: readonly BusinessStage[] = ["planning", "building", "about_to_open", "open_new", "open_running"];
export const HOME_STAGES: readonly HomeStage[] = ["just_moved", "settled"];
export const JOURNEY_ROLES: readonly JourneyRole[] = ["owner", "manager", "shift_lead", "staff"];
export const isBusinessStage = (v: unknown): v is BusinessStage => typeof v === "string" && (BUSINESS_STAGES as readonly string[]).includes(v);

export const STAGE_CHOICES: { id: BusinessStage; label: string; hint: string }[] = [
  { id: "planning", label: "Planning", hint: "No space or truck yet" },
  { id: "building", label: "Building out or getting permits", hint: "I have the space. It isn't ready yet" },
  { id: "about_to_open", label: "About to open", hint: "Opening in the next few weeks" },
  { id: "open_new", label: "Open, first months", hint: "Serving food, still new" },
  { id: "open_running", label: "Open and running", hint: "Serving food for months or years" },
];
export const HOME_STAGE_CHOICES: { id: HomeStage; label: string; hint: string }[] = [
  { id: "just_moved", label: "Just moved in", hint: "Still finding out what we have" },
  { id: "settled", label: "Settled in", hint: "We have the basics" },
];
export const ROLE_CHOICES: { id: JourneyRole; label: string; hint: string }[] = [
  { id: "owner", label: "Owner", hint: "It's my business" },
  { id: "manager", label: "Manager", hint: "I run it for the owner" },
  { id: "shift_lead", label: "Shift lead", hint: "I run some shifts" },
  { id: "staff", label: "Staff", hint: "I work there" },
];

/** Shift leads and staff join the owner's team; they don't set up a second business (review A15: ghost orgs). */
export const joinsATeam = (r: Maybe<JourneyRole> | undefined) => r === "shift_lead" || r === "staff";

/** The "Who is this for?" tiles. `typeId` is core opening.ts BUSINESS_TYPES (undefined for home). */
export const WHO_CHOICES: { id: JourneyWho; label: string; hint: string; typeId?: string }[] = [
  { id: "home", label: "My home", hint: "Kitchen, bathroom, food dates, kids and guests" },
  { id: "coffee_shop", label: "Coffee shop", hint: "Drinks, pastries, light food", typeId: "coffee_shop" },
  { id: "restaurant", label: "Restaurant", hint: "A kitchen that cooks meals", typeId: "restaurant" },
  { id: "food_truck", label: "Food truck or cart", hint: "A kitchen on wheels", typeId: "food_truck" },
  { id: "church_kitchen", label: "Church or community kitchen", hint: "Parish hall, meetinghouse", typeId: "community_kitchen" },
  { id: "event_booth", label: "Event booth", hint: "Food at a fair or festival", typeId: "event_booth" },
];

/** Which path a business type walks (BUSINESS_TYPES id → JourneyWho). */
export function whoOfType(typeId: string | undefined): JourneyWho {
  switch (typeId) {
    case "coffee_shop": case "bakery_cafe": return "coffee_shop";
    case "food_truck": case "taco_truck": return "food_truck";
    case "community_kitchen": return "church_kitchen";
    case "event_booth": return "event_booth";
    default: return "restaurant";
  }
}
/** A place set up before the journey existed: read its path from the org kind and permit. */
export function whoOfPlace(o: { orgKind?: OrgKind; permitType?: PermitType; typeId?: string }): JourneyWho {
  if (o.typeId) return whoOfType(o.typeId);
  if (o.orgKind === "home") return "home";
  if (o.orgKind === "coffee_shop") return "coffee_shop";
  if (o.permitType === "mobile_food_business") return "food_truck";
  if (o.permitType === "temporary_event") return "event_booth";
  if (o.orgKind === "community_space") return "church_kitchen";
  return "restaurant";
}

/* ─────────────── one field: Location.openingPhase (migrations 0065, 0066) ─────────────── */

/**
 * fix/review-safety stored the setup answer as planning / opening_soon / open (migration 0065), and its backfill still
 * copies those older answers from devices. The field now holds the five stages; these three are accepted on PATCH and
 * mapped here, the same way migration 0066 converted the stored rows.
 */
export type LegacyPhase = "planning" | "opening_soon" | "open";
export type PhaseInput = BusinessStage | LegacyPhase;
export const isPhaseInput = (v: unknown): v is PhaseInput => isBusinessStage(v) || v === "opening_soon" || v === "open";
export function stageFromPhase(v: PhaseInput): BusinessStage {
  return v === "opening_soon" ? "about_to_open" : v === "open" ? "open_running" : v;
}
/** The three-way answer the Open legally screens show (core opening.ts openingHeadline), from the stage. */
export function legacyPhase(s: BusinessStage | null | undefined): LegacyPhase | undefined {
  if (!s) return undefined;
  return s === "planning" ? "planning" : modeOf(s) === "planning" ? "opening_soon" : "open";
}

/* ─────────────── planning vs operating (J3, J4) ─────────────── */

export type PlaceMode = "planning" | "operating";
const PLANNING: ReadonlySet<BusinessStage> = new Set(["planning", "building", "about_to_open"]);

/** Absent stage = a place set up before this existed (they set up to run it): operating, as before. */
export function modeOf(stage: BusinessStage | null | undefined): PlaceMode {
  return stage && PLANNING.has(stage) ? "planning" : "operating";
}
export const isPlanning = (loc: Pick<Location, "openingPhase"> | null | undefined) => modeOf(loc?.openingPhase) === "planning";

/** Every day closed: nothing is expected, due, late or missed (schedule.ts dayMinutes → null). */
export const PLANNING_HOURS: WeeklyHours = { mon: "closed", tue: "closed", wed: "closed", thu: "closed", fri: "closed", sat: "closed", sun: "closed" };

/**
 * The hours the daily schedule runs on (J3). A place in planning mode isn't serving food, so no check is due or late
 * there, whatever hours setup guessed. Everything that builds a day (API day.ts, health, push, the mocks) reads this.
 */
export function scheduleHours(loc: Pick<Location, "openingPhase" | "hours">): WeeklyHours | undefined {
  return isPlanning(loc) ? PLANNING_HOURS : loc.hours;
}

/**
 * When food work starts at a place, for the food-handler clock (Utah Code 26B-7-413: the course "within 14 days" and
 * the permit "within 30 days" of starting food work). null = not yet: a place that isn't serving food has no food work.
 * Operating places with an openedOn start there; older operating places (no openedOn) keep the old start (startedOn).
 */
export function foodWorkStart(locs: readonly Pick<Location, "openingPhase" | "openedOn">[]): IsoDate | null | undefined {
  const operating = locs.filter((l) => !isPlanning(l));
  if (!operating.length) return null;
  const dates = operating.map((l) => l.openedOn).filter((d): d is IsoDate => !!d).sort();
  // Any operating place without a date (set up before 2026-10-01): no later start than the person's own, as before.
  return operating.some((l) => !l.openedOn) ? undefined : dates[0];
}

/** The clock start for one person: the later of their start and the first day food was served. */
export function clockStart(startedOn: IsoDate, work: IsoDate | null | undefined): IsoDate | null {
  if (work === null) return null;
  if (!work) return startedOn;
  return work > startedOn ? work : startedOn;
}

/** Moving to operating from planning (J4): the server stamps openedOn with the local day; nothing else does. */
export function openingPatch(prev: Pick<Location, "openingPhase" | "openedOn">, next: BusinessStage, today: IsoDate): Pick<Location, "openingPhase" | "openedOn"> {
  const fromPlanning = modeOf(prev.openingPhase) === "planning";
  if (modeOf(next) === "operating" && fromPlanning) return { openingPhase: next, openedOn: today };
  // Open → open (new → running), or back to planning: the day it opened is kept for the record.
  return { openingPhase: next, ...(prev.openedOn ? { openedOn: prev.openedOn } : {}) };
}

/* ─────────────── unknowns (J1, J2) ─────────────── */

export type FactKey = "county" | "permit" | "hours" | "menu" | "stage";
/** "stage" (2026-10-06, sweet first): a place set up without an inspection record and without the stage question starts as planning
 *  (nothing is late) with "stage" to confirm; the path's first thing is then the one-tap "where are you" (personalPath). */
export const FACT_KEYS: readonly FactKey[] = ["county", "permit", "hours", "menu", "stage"];
export const isFactKey = (v: unknown): v is FactKey => typeof v === "string" && (FACT_KEYS as readonly string[]).includes(v);

/** When each guess starts to decide something, and why (asked then, not at setup). */
export const FACT_MOMENT: Record<FactKey, { from: BusinessStage; label: string; why: string; to: string }> = {
  county: { from: "building", label: "Which county you'll serve in", why: "Your county's health department reviews your plans and gives your permit, so you get their steps, not another county's.", to: "/settings#location" },
  permit: { from: "building", label: "Which permit you need", why: "A café, a home kitchen and a truck each get a different permit and inspection. The right one means only your steps show.", to: "/settings#location" },
  hours: { from: "about_to_open", label: "Your real opening hours", why: "Your opening and closing checks run on these hours. Wrong hours mean a reminder when nobody is there.", to: "/settings#hours" },
  stage: { from: "planning", label: "Whether you serve food yet", why: "Your next three change with it: an open place gets this morning's checks, a planner gets the permit steps.", to: "/start/stage" },
  menu: { from: "about_to_open", label: "What you'll serve", why: "Each item adds the checks that keep it safe, and nothing you don't serve.", to: "/settings#menu" },
};

const stageIndex = (s: BusinessStage) => BUSINESS_STAGES.indexOf(s);
/** The unconfirmed facts whose moment has come at this stage (J2). An unknown stage asks all of them. */
export function factsToConfirm(unconfirmed: readonly FactKey[] | undefined, stage: BusinessStage | undefined): FactKey[] {
  return (unconfirmed ?? []).filter((k) => !stage || stageIndex(stage) >= stageIndex(FACT_MOMENT[k].from));
}

/** How a fact reads on screen (J1): the value only when confirmed. */
export function factLine(key: FactKey, value: string | undefined, unconfirmed: readonly FactKey[] | undefined): string {
  const open = (unconfirmed ?? []).includes(key);
  if (key === "county") return !value ? "County: not set yet" : open ? `${countyLabel(value)} (to confirm)` : countyLabel(value);
  return open ? `${value ?? ""} (to confirm)`.trim() : value ?? "";
}

/** Saving a field confirms it; `confirm` confirms without a change ("Yes, that's right"). */
export function confirmFacts(unconfirmed: readonly FactKey[] | undefined, saved: readonly FactKey[]): FactKey[] | undefined {
  const left = (unconfirmed ?? []).filter((k) => !saved.includes(k));
  return left.length ? left : undefined;
}

/**
 * The unconfirmed list after a location save (J1): a field the owner saved is confirmed, `confirm` confirms without a
 * change, and county "" ("Not sure yet") is unconfirmed again. The API and both mocks use this, so they agree.
 */
export function unconfirmedAfter(prev: readonly FactKey[] | undefined, patch: { county?: unknown; permitType?: unknown; hours?: unknown; openAt?: unknown; closeAt?: unknown; menuItemIds?: unknown; stage?: unknown; openingPhase?: unknown; confirm?: readonly FactKey[] }): FactKey[] | undefined {
  const saved: FactKey[] = [...(patch.confirm ?? [])];
  if (patch.openingPhase !== undefined) saved.push("stage");
  if (typeof patch.county === "string" && patch.county) saved.push("county");
  if (patch.permitType !== undefined) saved.push("permit");
  if (patch.hours !== undefined || patch.openAt !== undefined || patch.closeAt !== undefined) saved.push("hours");
  if (patch.menuItemIds !== undefined || patch.stage !== undefined) saved.push("menu");
  let next = confirmFacts(prev, saved);
  if (patch.county === "") next = [...new Set([...(next ?? []), "county" as FactKey])];
  return next;
}

/* ─────────────── the path (J5, J6) ─────────────── */

export type InventoryAnswer = "have" | "need" | "unsure";
/** Stored on the location (business) or in the home profile (home). */
export interface PlaceJourney {
  /** BUSINESS_TYPES id picked at setup ("coffee_shop"), for the path; absent on older places. */
  typeId?: string;
  /** The setup person's role answer ("not_sure" kept as such). */
  role?: Maybe<JourneyRole>;
  done?: string[];
  /** "Not now": moved behind the rest, still in "See everything". */
  later?: string[];
  /** "What do you have?" answers by item key (shop kit keys for a business, have-item ids for a home). */
  inventory?: Record<string, InventoryAnswer>;
  /**
   * "Is this you?" (isthisyou.ts): the FDA Form 3-A items of the 3 things we keep the place ahead of, from its last inspection.
   * They are the first 3 path items until done or put off (ahead.ts AHEAD).
   */
  ahead?: number[];
}

export interface PathAction { label: string; to: string }
export interface PathItem {
  id: string;
  /** The concrete moment, verb first ("Check the fridge is 40°F or below"). */
  title: string;
  /** One plain sentence: the good it serves for the person, their guests or customers, their team (J6). */
  why: string;
  action: PathAction;
  minutes: number;
  /** 1–5: how much it protects people or saves the owner from trouble. Per stage where it differs. */
  benefit: number | Partial<Record<JourneyStage, number>>;
  who: readonly JourneyWho[];
  stages: readonly JourneyStage[];
  /** Roles it's for (default: owner and manager). */
  roles?: readonly JourneyRole[];
  /** Only for homes with kids / guests. */
  needs?: "kids" | "guests";
  /** Where the why comes from (a rule, a research row), or "Our advice". */
  source: string;
}

const BIZ: readonly JourneyWho[] = ["coffee_shop", "restaurant", "food_truck", "church_kitchen", "event_booth"];
const PERMITTED: readonly JourneyWho[] = ["coffee_shop", "restaurant", "food_truck", "event_booth"];
const EARLY: readonly BusinessStage[] = ["planning", "building"];
const OPEN: readonly BusinessStage[] = ["open_new", "open_running"];
const ALL_BIZ: readonly BusinessStage[] = BUSINESS_STAGES;
const HOME: readonly JourneyWho[] = ["home"];
const ANY_HOME: readonly HomeStage[] = HOME_STAGES;

/**
 * The catalog. Benefit and minutes are our estimates (Our advice), written down so the ranking can be argued with:
 * benefit 5 = it keeps someone from getting sick or keeps the owner from building or buying something twice.
 */
const PATH_CACHE = new Map<UsState, readonly PathItem[]>();
/** The catalog with a state's rules and research rows (an item that states a rule the state has no words for is left out). */
export function pathItemsFor(state?: UsState): readonly PathItem[] {
  const key = stateOrDefault(state);
  let items = PATH_CACHE.get(key);
  if (!items) PATH_CACHE.set(key, (items = buildPathItems(key)));
  return items;
}
function buildPathItems(state: UsState): readonly PathItem[] {
  const w = words(state);
  /** " (R392-100)" after a Food Code section, when the state has a rule that adopts it. */
  const adopts = w.foodCodeRule ? ` (${w.foodCodeRule})` : "";
  const items: PathItem[] = [
  // ── business, before opening ──
  { id: "permit-path", title: "See your path to a permit, step by step", why: "Plan review before you build, permit before you serve. In that order nothing gets torn out and built twice.", action: { label: "Show my steps", to: "/open" }, minutes: 3, benefit: 5, who: PERMITTED, stages: [...EARLY, "about_to_open"], source: cite(`Food Code 8-201.11, 8-302.11${adopts}`, w.permitResearchDoc) },
  { id: "equipment-list", title: "See what to buy, with the rule for each", why: "Buying the right thermometer, sink supplies and bins once is cheaper than buying them again after an inspection.", action: { label: "See the list", to: "/supplies/kit" }, minutes: 5, benefit: 4, who: BIZ, stages: [...EARLY, "about_to_open"], source: "core shopkit.ts (each item names its Food Code section)" },
  { id: "have-walk", title: "What do you have? Walk it room by room", why: "You find what's missing now (soap at every hand sink, a lidded bin in the restroom), not on inspection day.", action: { label: "Start the walk-through", to: "/start/have" }, minutes: 5, benefit: { planning: 4, building: 4, about_to_open: 4, open_new: 3, open_running: 2 }, who: BIZ, stages: ALL_BIZ, source: "Food Code 6-301.11, 6-301.12, 6-301.14, 5-501.17" },
  { id: "plan-review", title: "Call your county about plan review", why: "They approve your layout before you build, so one call now saves moving a sink later.", action: { label: "See who to call", to: "/open" }, minutes: 15, benefit: 5, who: ["coffee_shop", "restaurant", "food_truck"], stages: ["building"], source: cite(`Food Code 8-201.11${adopts}`, w.truckPlanReviewRule) },
  ...(w.managerWhy && w.managerSource ? [({ id: "manager-cert", title: "Get your food safety manager certificate started", why: w.managerWhy!, action: { label: "See what it takes", to: "/open" }, minutes: 5, benefit: 3, who: ["coffee_shop", "restaurant", "food_truck"], stages: [...EARLY, "about_to_open"], source: w.managerSource! }) satisfies PathItem] : []),
  { id: "learn-basics", title: "Learn the three habits that keep customers from getting sick", why: "Sick workers kept off food, washed hands, cold food kept cold. Know why, and the daily checks make sense.", action: { label: "Start a 3-minute lesson", to: "/learn" }, minutes: 10, benefit: 3, who: BIZ, stages: [...EARLY, "about_to_open"], source: "Food Code 2-201.11, 2-301.12, 3-501.16 (the three worker controls)" },
  { id: "invite-team", title: "Add your team", why: "Each person gets their own health check-in and lessons, so keeping food safe isn't only on you.", action: { label: "Add someone", to: "/people/add" }, minutes: 5, benefit: { about_to_open: 3, open_new: 3, open_running: 2 }, who: BIZ, stages: ["about_to_open", ...OPEN], source: w.handlerPermitSource ?? "Our advice" },
  // ── business, open ──
  { id: "calibrate", title: "Check your thermometer in ice water", why: "Every temperature you log is only as true as the thermometer that took it.", action: { label: "Do it now, 2 minutes", to: "/do?log=calibration" }, minutes: 2, benefit: 4, who: BIZ, stages: ["open_new"], source: "Food Code 4-502.11(B)" },
  { id: "opening-checks", title: "Log the fridge and stock the hand sink before you open", why: "Cold food stays cold and hands get washed before the first customer. That is what keeps them well.", action: { label: "Start this morning's list", to: "/do" }, minutes: 3, benefit: 5, who: BIZ, stages: OPEN, source: `Food Code 3-501.16, 6-301.11${adopts}` },
  { id: "binder", title: "See your inspection binder", why: "Every check you log lands here, so when the inspector walks in you hand them one page.", action: { label: "Open the binder", to: "/prove" }, minutes: 2, benefit: 3, who: BIZ, stages: OPEN, source: "Food Code 8-402.11 (access for inspection)" },
  { id: "our-way", title: "Teach a job your way", why: "Your team learns a job the way you do it. The screen shows your steps, not ours.", action: { label: "Set my steps", to: "/settings#our-way" }, minutes: 5, benefit: 2, who: BIZ, stages: ["open_running"], source: "Our advice" },
  // ── home ──
  { id: "home-fridge", title: "Check the fridge is 40°F or below", why: "A cold fridge is what keeps leftovers and milk from growing germs that make your family sick.", action: { label: "Show me how", to: "/rooms/guide/fridge-temp" }, minutes: 3, benefit: 5, who: HOME, stages: ANY_HOME, source: "home-food-safety H2, H3" },
  { id: "home-kitchen-have", title: "Walk the kitchen: what do you have?", why: "A fridge thermometer, a board for raw meat and soap at the sink. We list only what's missing.", action: { label: "Walk the kitchen", to: "/start/have?room=kitchen" }, minutes: 4, benefit: { just_moved: 4, settled: 2 }, who: HOME, stages: ANY_HOME, source: "home-food-safety H2, H19, H20" },
  { id: "home-bathroom-have", title: "Bathroom basics: what do you have?", why: "Soap, your own towel and a lidded bin keep germs from passing between the people who share it.", action: { label: "Walk the bathroom", to: "/start/have?room=bathroom" }, minutes: 3, benefit: { just_moved: 3, settled: 2 }, who: HOME, stages: ANY_HOME, source: "grooming-and-guests GR1, GR3" },
  { id: "home-ten", title: "Pick this week's three chores", why: "Food and bathroom first, so even a short week keeps everyone well.", action: { label: "Show me three", to: "/?ten=1#ten" }, minutes: 3, benefit: { just_moved: 2, settled: 3 }, who: HOME, stages: ANY_HOME, source: "Our advice" },
  { id: "home-guest-bath", title: "Get the bathroom ready for guests", why: "A guest who can wash with soap and dry on a clean towel goes home as well as they came.", action: { label: "Open the guide", to: "/rooms/guide/guest-bathroom-basics" }, minutes: 5, benefit: 3, who: HOME, stages: ANY_HOME, needs: "guests", source: "grooming-and-guests GR1, GR3" },
  { id: "home-kid-turn", title: "Give a child a chore they can do alone", why: "A child who does a real job well learns they help keep the family well.", action: { label: "Hand the phone over", to: "/kids" }, minutes: 2, benefit: 2, who: HOME, stages: ANY_HOME, needs: "kids", source: "Our advice" },
  { id: "home-clippers", title: "Clean and store shavers and clippers", why: "A clean blade kept dry and out of reach protects skin, kids and guests.", action: { label: "Open the guide", to: "/rooms/guide/clipper-disinfect" }, minutes: 5, benefit: 2, who: HOME, stages: ANY_HOME, source: "grooming-and-guests GS1–GS3, GT1" },
  { id: "home-beard-sink", title: "Get beard hair out of the sink and drain", why: "A clear drain and a clean sink keep the next person's hands and toothbrush clean.", action: { label: "Open the guide", to: "/rooms/guide/beard-sink-drain" }, minutes: 5, benefit: 1, who: HOME, stages: ANY_HOME, source: "grooming-and-guests GB2–GB7" },
  ];
  return items;
}
export const PATH_ITEMS: readonly PathItem[] = pathItemsFor("UT");

export const pathItem = (id: string, state?: UsState) => pathItemsFor(state).find((p) => p.id === id);

export interface PathContext {
  who: JourneyWho;
  stage: Maybe<JourneyStage> | undefined;
  role?: Maybe<JourneyRole>;
  household?: { kids?: boolean; guests?: boolean };
  unconfirmed?: readonly FactKey[];
  journey?: PlaceJourney;
  /** Items the app already knows are done (a calibration logged, a second person on the team). */
  auto?: readonly string[];
  /** The state whose rules the items cite (default Utah). */
  state?: UsState;
}

export interface PathView {
  /** The next 3 (or fewer), best first. */
  next: PathItem[];
  /** Everything else that fits, folded behind "See everything": not done yet, then put off. */
  later: PathItem[];
  done: PathItem[];
  total: number;
}

const benefitAt = (p: PathItem, stage: JourneyStage | undefined) => (typeof p.benefit === "number" ? p.benefit : (stage && p.benefit[stage]) ?? Math.max(...Object.values(p.benefit).map(Number)));
/** Benefit per minute (J5). Exported for the test and for anyone who wants to argue with the order. */
export const pathScore = (p: PathItem, stage: JourneyStage | undefined) => benefitAt(p, stage) / p.minutes;

/** The "to confirm" items (J2), made for this place. */
function factItems(c: PathContext): PathItem[] {
  if (c.who === "home") return [];
  const stage = c.stage && c.stage !== NOT_SURE && isBusinessStage(c.stage) ? c.stage : undefined;
  return factsToConfirm(c.unconfirmed, stage).filter((k) => k !== "stage").map((k) => ({
    id: `confirm-${k}`, title: `Set ${FACT_MOMENT[k].label.charAt(0).toLowerCase()}${FACT_MOMENT[k].label.slice(1)}`, why: FACT_MOMENT[k].why,
    // At its own moment (the stage it starts to decide something) it ranks with the sweet items; past it, behind them (sweet
    // first: a guess to confirm never pushes this morning's checks out of the 3 things; it waits in "See everything" and its seed).
    action: { label: "Set it now", to: FACT_MOMENT[k].to }, minutes: 2, benefit: stage === FACT_MOMENT[k].from ? 3 : 0.02, who: BIZ, stages: ALL_BIZ, source: "Our advice",
  }));
}

/** The place's "Is this you?" things as path items (business only; a home never has them). Two items with one check count once. */
function aheadItems(c: PathContext, stage: JourneyStage): PathItem[] {
  if (c.who === "home") return [];
  const seen = new Set<string>();
  const out: PathItem[] = [];
  for (const n of (c.journey?.ahead ?? []).filter(isAheadItem).slice(0, AHEAD_MAX)) {
    const d = AHEAD[n]!;
    if (seen.has(d.title)) continue;
    seen.add(d.title);
    out.push({ id: `ahead-${n}`, title: d.title, why: d.help, action: d.action, minutes: d.minutes, benefit: 5, who: [c.who], stages: [stage], source: `Your last inspection; FDA Food Code 2022 Annex 7, Form 3-A item ${n}` });
  }
  return out;
}

/**
 * The personal path (J5): the items for this who, stage and role, ranked by benefit per minute (ties keep catalog
 * order), done and put-off ones moved out of the top. A "Not sure" stage reads as the earliest stage for its kind
 * (nothing assumes you're open) plus the "Where are you?" question first.
 */
export function personalPath(c: PathContext, n = 3): PathView {
  const role: JourneyRole = !c.role || c.role === NOT_SURE ? "owner" : c.role;
  const stage: JourneyStage | undefined = c.stage && c.stage !== NOT_SURE ? c.stage : undefined;
  const effective: JourneyStage = stage ?? (c.who === "home" ? "just_moved" : "planning");
  const done = new Set([...(c.journey?.done ?? []), ...(c.auto ?? [])]);
  const later = new Set(c.journey?.later ?? []);
  const fits = (p: PathItem) => p.who.includes(c.who) && p.stages.includes(effective) && (p.roles ?? ["owner", "manager"]).includes(role)
    && (!p.needs || (p.needs === "kids" ? !!c.household?.kids : !!c.household?.guests));
  // The stage unknown, or guessed at setup ("stage" to confirm, sweet first): one tap first, since the next three change with it.
  const askStage = !stage || (c.who !== "home" && (c.unconfirmed ?? []).includes("stage"));
  const ask: PathItem[] = !askStage ? [] : [{
    id: "where-are-you", title: c.who === "home" ? "Tell us where you are: just moved in, or settled" : "Tell us where you are: planning, building or open",
    why: c.who === "home" ? "Your next three change with it. Just moved in: we start with what you have. Settled: with what's due." : "Your next three change with it. A planner gets permits first. An open café gets this morning's checks.", action: { label: "Answer one question", to: "/start/stage" },
    minutes: 1, benefit: 5, who: [c.who], stages: [effective], source: "Our advice",
  }];
  const ahead = aheadItems(c, effective);
  const pool = [...ahead, ...ask, ...factItems(c), ...pathItemsFor(c.state).filter(fits)];
  const order = new Map(pool.map((p, i) => [p.id, i]));
  const first = new Set(ahead.map((p) => p.id));
  // "Is this you?" things come first (the owner chose them from their own inspection), in their order; the rest by benefit per minute.
  const rank = (a: PathItem, b: PathItem) => Number(first.has(b.id)) - Number(first.has(a.id)) || (first.has(a.id) ? 0 : pathScore(b, effective) - pathScore(a, effective)) || order.get(a.id)! - order.get(b.id)!;
  const open = pool.filter((p) => !done.has(p.id) && !later.has(p.id)).sort(rank);
  const putOff = pool.filter((p) => !done.has(p.id) && later.has(p.id)).sort(rank);
  return { next: open.slice(0, n), later: [...open.slice(n), ...putOff], done: pool.filter((p) => done.has(p.id)), total: pool.length };
}

/** Marking an item (PATCH …/journey). `undo` clears it. Pure, so the API and the mocks agree. */
export function markPath(j: PlaceJourney | undefined, id: string, how: "done" | "later" | "undo"): PlaceJourney {
  const done = (j?.done ?? []).filter((x) => x !== id);
  const later = (j?.later ?? []).filter((x) => x !== id);
  if (how === "done") done.push(id);
  if (how === "later") later.push(id);
  return { ...(j ?? {}), done, later };
}

/** Checks a journey patch from a client: one item id and how, or one inventory answer. Returns the plain problem. */
export function journeyPatchProblem(b: Record<string, unknown>): string | null {
  if (b.item !== undefined) {
    if (typeof b.item !== "string" || !/^[a-z0-9-]{2,60}$/.test(b.item)) return '"item" must be a path item id.';
    if (!["done", "later", "undo"].includes(b.how as string)) return '"how" must be done, later or undo.';
  }
  if (b.inventory !== undefined) {
    const v = b.inventory as Record<string, unknown>;
    if (typeof v !== "object" || v === null || typeof v.key !== "string" || !/^[a-z0-9-]{2,60}$/.test(v.key) || !["have", "need", "unsure"].includes(v.answer as string)) return '"inventory" must be { key, answer: have, need or unsure }.';
  }
  if (b.role !== undefined && b.role !== NOT_SURE && !(JOURNEY_ROLES as readonly string[]).includes(b.role as string)) return '"role" must be owner, manager, shift_lead, staff or not_sure.';
  if (b.item === undefined && b.inventory === undefined && b.role === undefined) return "Nothing to change.";
  return null;
}

/** Applies a checked journey patch. */
export function applyJourneyPatch(j: PlaceJourney | undefined, b: { item?: string; how?: "done" | "later" | "undo"; inventory?: { key: string; answer: InventoryAnswer }; role?: Maybe<JourneyRole> }): PlaceJourney {
  let next: PlaceJourney = { ...(j ?? {}) };
  if (b.item && b.how) next = markPath(next, b.item, b.how);
  if (b.inventory) next = { ...next, inventory: { ...(next.inventory ?? {}), [b.inventory.key]: b.inventory.answer } };
  if (b.role) next = { ...next, role: b.role };
  return next;
}

/* ─────────────── what the planning Today says (J3) ─────────────── */

export const GETTING_READY = {
  title: "Getting ready to open",
  lead: "Nothing here is late. Daily checks start the day you tell us you're open.",
  openTitle: "Serving customers from today?",
  openWhy: "From today your opening and closing checks run on your hours, and each food worker's 30-day permit count starts.",
  openButton: "Yes, we're open",
} as const;

/* ─────────────── a child's turn on the parent's phone (review C2) ─────────────── */

/**
 * Chores a child can do alone (Our advice: no cleaner, no heat, no blade, nothing to plug in). Steps are the guide's own,
 * word for word; `steps` keeps only the ones a child does (the towels go in the wash; a grown-up runs the machine).
 */
export const KID_CHORES: readonly { choreId: string; steps?: readonly number[] }[] = [
  { choreId: "hand-towels", steps: [0, 1] },
  { choreId: "dusting" },
  { choreId: "make-bed" },
];
/** The parent's line after a child's turn: credit where earned, with the time. */
export const kidDoneLine = (name: string, time: string) => `${name} did this, ${time}`;
